import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "../../../../shared/components/LanguageSwitcher/index.jsx";
import styles from "../employeeRegister/employeeRegister.module.css";
import defaultLogo from "../../../../images/logo2.png";
import appConfig from "../../../../config/appConfig.js";
import { useTenantBranding } from "../../../../shared/branding/TenantBrandingProvider.jsx";
import {
  logout,
  setToken,
  setUserPermissions,
} from "../../../../shared/lib/auth/authHelper.js";
import { resolveAcademyEntryRoute } from "../../../academy/lib/resolveAcademyEntryRoute";

export default function EmployeeRegisterPage() {
  const { t } = useTranslation();
  const { branding } = useTenantBranding();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState("email");

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    password: "",
    code: "",
  });

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [googleConfigLoading, setGoogleConfigLoading] = useState(true);
  const [googleEnabled, setGoogleEnabled] = useState(false);
  const [googleClientId, setGoogleClientId] = useState("");
  const [googleRedirectUri, setGoogleRedirectUri] = useState("");
  const [googleFrontendSuccessUrl, setGoogleFrontendSuccessUrl] = useState("");
  const [googleFrontendErrorUrl, setGoogleFrontendErrorUrl] = useState("");

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [verificationExpiresAt, setVerificationExpiresAt] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  const logoSrc = branding?.logo_url || defaultLogo;

  const fallbackRedirectUri = useMemo(() => {
    return `${window.location.origin}/employee-register`;
  }, []);

  const fallbackFrontendSuccessUrl = useMemo(() => {
    return `${window.location.origin}/menu/dashboard`;
  }, []);

  const fallbackFrontendErrorUrl = useMemo(() => {
    return `${window.location.origin}/employee-register`;
  }, []);

  const isVerificationExpired =
    step === "code" && verificationExpiresAt && secondsLeft <= 0;

  const formatRemainingTime = (totalSeconds) => {
    const safe = Math.max(0, Number(totalSeconds || 0));
    const minutes = Math.floor(safe / 60);
    const seconds = safe % 60;
    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(
      2,
      "0"
    )}`;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setError("");
    setSuccess("");

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetAll = () => {
    setFormData({
      first_name: "",
      last_name: "",
      email: "",
      password: "",
      code: "",
    });
    setStep("email");
    setVerificationExpiresAt(null);
    setSecondsLeft(0);
  };

  const validateEmailStep = () => {
    if (!formData.email.trim()) {
      setError(
        t("error_required_fields", {
          defaultValue: "Please fill in all required fields",
        })
      );
      return false;
    }

    setError("");
    return true;
  };

  const validateCodeStep = () => {
    if (isVerificationExpired) {
      setError(
        t("verification_code_expired", {
          defaultValue: "Verification code has expired. Please request a new one.",
        })
      );
      return false;
    }

    if (!formData.code.trim()) {
      setError(
        t("error_required_fields", {
          defaultValue: "Please fill in all required fields",
        })
      );
      return false;
    }

    setError("");
    return true;
  };

  const validateProfileStep = () => {
    if (
      !formData.first_name.trim() ||
      !formData.last_name.trim() ||
      !formData.email.trim() ||
      !formData.code.trim() ||
      !formData.password
    ) {
      setError(
        t("error_required_fields", {
          defaultValue: "Please fill in all required fields",
        })
      );
      return false;
    }

    if ((formData.password || "").length < 8) {
      setError(
        t("password_min_length", {
          defaultValue: "Password must be at least 8 characters",
        })
      );
      return false;
    }

    setError("");
    return true;
  };

  const loadPermissionsAndEnter = async (accessToken) => {
    const permissionsResponse = await fetch(
      `${appConfig.API_BASE_URL}/auth/me/permissions`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        credentials: "include",
      }
    );

    const permissionsData = await permissionsResponse.json().catch(() => ({}));

    if (!permissionsResponse.ok) {
      logout();
      throw new Error(
        permissionsData.detail ||
          permissionsData.message ||
          "Failed to load permissions"
      );
    }

    const permissions = Array.isArray(permissionsData) ? permissionsData : [];
    setUserPermissions(permissions);

    localStorage.removeItem("session-exp-ms");
    window.dispatchEvent(new Event("token-changed"));
    window.dispatchEvent(new Event("permissions-updated"));

    const entryRoute = await resolveAcademyEntryRoute(accessToken);
    navigate(entryRoute, { replace: true });
    return entryRoute;
  };

  const applyVerificationTtl = (expiresInSeconds) => {
    const ttlSeconds =
      Number.isFinite(Number(expiresInSeconds)) && Number(expiresInSeconds) > 0
        ? Number(expiresInSeconds)
        : 180;

    const expiresAt = Date.now() + ttlSeconds * 1000;
    setVerificationExpiresAt(expiresAt);
    setSecondsLeft(ttlSeconds);
  };

  const handleRequestCode = async (event) => {
    event.preventDefault();
    if (!validateEmailStep()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${appConfig.API_BASE_URL}/auth/employee/register-public/request-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: formData.email.trim(),
          }),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          responseData.detail ||
            responseData.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );
        return;
      }

      applyVerificationTtl(responseData?.expires_in_seconds);
      setStep("code");
      setFormData((prev) => ({
        ...prev,
        code: "",
      }));
      setSuccess(
        t("verification_code_sent", {
          defaultValue: "We sent a verification code to your email",
        })
      );
    } catch {
      setError(t("error_generic", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (event) => {
    event.preventDefault();
    if (!validateCodeStep()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${appConfig.API_BASE_URL}/auth/employee/register-public/verify-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: formData.email.trim(),
            code: formData.code.trim(),
          }),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          responseData.detail ||
            responseData.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );
        return;
      }

      setStep("profile");
      setSuccess(
        t("verification_code_confirmed", {
          defaultValue: "Email verified successfully",
        })
      );
    } catch {
      setError(t("error_generic", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteRegister = async (event) => {
    event.preventDefault();
    if (!validateProfileStep()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const payload = {
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        email: formData.email.trim(),
        code: formData.code.trim(),
        password: formData.password,
      };

      const response = await fetch(
        `${appConfig.API_BASE_URL}/auth/employee/register-public/complete`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(payload),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          responseData.detail ||
            responseData.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );
        return;
      }

      setSuccess(
        t("employee_created_success", {
          defaultValue: "Employee created successfully",
        })
      );
      resetAll();
    } catch {
      setError(t("error_generic", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!validateEmailStep()) return;

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(
        `${appConfig.API_BASE_URL}/auth/employee/register-public/request-code`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            email: formData.email.trim(),
          }),
        }
      );

      const responseData = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(
          responseData.detail ||
            responseData.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );
        return;
      }

      applyVerificationTtl(responseData?.expires_in_seconds);
      setFormData((prev) => ({
        ...prev,
        code: "",
      }));
      setSuccess(
        t("verification_code_resent", {
          defaultValue: "Verification code sent again",
        })
      );
    } catch {
      setError(t("error_generic", { defaultValue: "Something went wrong" }));
    } finally {
      setLoading(false);
    }
  };

  const handleChangeEmail = () => {
    setError("");
    setSuccess("");
    setVerificationExpiresAt(null);
    setSecondsLeft(0);
    setFormData((prev) => ({
      ...prev,
      code: "",
      first_name: "",
      last_name: "",
      password: "",
    }));
    setStep("email");
  };

  const handleGoogleRegister = () => {
    setError("");
    setSuccess("");

    if (!googleEnabled) {
      return;
    }

    if (!googleClientId) {
      setError(
        t("google_client_id_missing", {
          defaultValue: "Google OAuth is not configured for this tenant yet",
        })
      );
      return;
    }

    const redirectUri = googleRedirectUri || fallbackRedirectUri;

    const params = new URLSearchParams({
      client_id: googleClientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "offline",
      prompt: "select_account",
    });

    window.location.assign(
      `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`
    );
  };

  const handleSocialStub = (providerName) => {
    setError("");
    setSuccess("");

    setError(
      t("social_register_not_ready", {
        defaultValue: `${providerName} registration is not connected yet`,
        provider: providerName,
      })
    );
  };

  useEffect(() => {
    let cancelled = false;

    const loadGoogleConfig = async () => {
      try {
        setGoogleConfigLoading(true);

        const response = await fetch(
          `${appConfig.API_BASE_URL}/public/crmGoogleOAuth`,
          {
            method: "GET",
            credentials: "include",
          }
        );

        const data = await response.json().catch(() => ({}));

        if (!response.ok) {
          if (cancelled) return;

          setGoogleEnabled(false);
          setGoogleClientId("");
          setGoogleRedirectUri("");
          setGoogleFrontendSuccessUrl("");
          setGoogleFrontendErrorUrl("");
          return;
        }

        if (cancelled) return;

        setGoogleEnabled(!!data?.enabled);
        setGoogleClientId(data?.client_id || "");
        setGoogleRedirectUri(data?.redirect_uri || fallbackRedirectUri);
        setGoogleFrontendSuccessUrl(
          data?.frontend_success_url || fallbackFrontendSuccessUrl
        );
        setGoogleFrontendErrorUrl(
          data?.frontend_error_url || fallbackFrontendErrorUrl
        );
      } catch {
        if (cancelled) return;

        setGoogleEnabled(false);
        setGoogleClientId("");
        setGoogleRedirectUri("");
        setGoogleFrontendSuccessUrl("");
        setGoogleFrontendErrorUrl("");
      } finally {
        if (!cancelled) {
          setGoogleConfigLoading(false);
        }
      }
    };

    loadGoogleConfig();

    return () => {
      cancelled = true;
    };
  }, [
    fallbackRedirectUri,
    fallbackFrontendSuccessUrl,
    fallbackFrontendErrorUrl,
  ]);

  useEffect(() => {
    if (!verificationExpiresAt || step !== "code") return;

    const tick = () => {
      const diff = Math.max(
        0,
        Math.ceil((verificationExpiresAt - Date.now()) / 1000)
      );
      setSecondsLeft(diff);
    };

    tick();
    const interval = window.setInterval(tick, 1000);

    return () => window.clearInterval(interval);
  }, [verificationExpiresAt, step]);

  useEffect(() => {
    if (step !== "code") return;
    if (!verificationExpiresAt) return;
    if (secondsLeft > 0) return;

    setError(
      t("verification_code_expired", {
        defaultValue: "Verification code has expired. Please request a new one.",
      })
    );
  }, [secondsLeft, step, verificationExpiresAt, t]);

  useEffect(() => {
    const search = new URLSearchParams(location.search);
    const code = search.get("code");
    const oauthError = search.get("error");

    if (oauthError) {
      const redirectTarget =
        googleFrontendErrorUrl || fallbackFrontendErrorUrl || "";

      if (redirectTarget && redirectTarget !== window.location.href) {
        window.history.replaceState({}, document.title, redirectTarget);
      }

      setError(
        t("google_oauth_cancelled", {
          defaultValue: "Google authorization was cancelled or failed",
        })
      );
      return;
    }

    if (!code) return;
    if (!googleEnabled) return;

    let cancelled = false;

    const finishGoogleAuth = async () => {
      try {
        setGoogleLoading(true);
        setError("");
        setSuccess("");

        logout();

        const response = await fetch(
          `${appConfig.API_BASE_URL}/auth/employee/google`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            credentials: "include",
            body: JSON.stringify({ code }),
          }
        );

        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            responseData.detail ||
              responseData.message ||
              t("error_generic", { defaultValue: "Something went wrong" })
          );
        }

        const accessToken = responseData?.access_token;
        if (!accessToken) {
          throw new Error(
            t("google_access_token_missing", {
              defaultValue: "Google login did not return access token",
            })
          );
        }

        if (cancelled) return;

        setToken(accessToken);
        const entryRoute = await loadPermissionsAndEnter(accessToken);

        const successTarget =
          entryRoute === "/menu/academy"
            ? `${window.location.origin}/menu/academy`
            : googleFrontendSuccessUrl || fallbackFrontendSuccessUrl;
        if (successTarget) {
          window.history.replaceState({}, document.title, successTarget);
        }
      } catch (err) {
        if (cancelled) return;
        logout();
        setError(
          err?.message ||
            t("error_generic", { defaultValue: "Something went wrong" })
        );

        const errorTarget = googleFrontendErrorUrl || fallbackFrontendErrorUrl;
        if (errorTarget) {
          window.history.replaceState({}, document.title, errorTarget);
        }
      } finally {
        if (!cancelled) {
          setGoogleLoading(false);
          const cleanUrl =
            `${window.location.origin}${window.location.pathname}`;
          window.history.replaceState({}, document.title, cleanUrl);
        }
      }
    };

    finishGoogleAuth();

    return () => {
      cancelled = true;
    };
  }, [
    location.search,
    navigate,
    t,
    googleEnabled,
    googleFrontendSuccessUrl,
    googleFrontendErrorUrl,
    fallbackFrontendSuccessUrl,
    fallbackFrontendErrorUrl,
  ]);

  const showSocialSection = !googleConfigLoading && googleEnabled;
  const formDisabled = loading || googleLoading;

  return (
    <div
      className={styles.page}
      style={{ background: "var(--tenant-login-bg)" }}
    >
      <div className={styles.shell}>
        <div className={styles.leftSide}>
          <div className={styles.brandTop}>
            <img
              src={logoSrc}
              alt={branding?.company_name || "VelSoft CRM"}
              className={styles.brandLogo}
            />
          </div>

          <div className={styles.illustrationWrap}>
            <div className={styles.illustrationCard}>
              <div className={styles.floatingBadge}>✓</div>

              <div className={styles.mockWindow}>
                <div className={styles.mockHeader}>
                  <span />
                  <span />
                  <span />
                </div>

                <div className={styles.mockBody}>
                  <div className={styles.mockSidebar} />
                  <div className={styles.mockContent}>
                    <div className={styles.mockLineLg} />
                    <div className={styles.mockLineMd} />
                    <div className={styles.mockGrid}>
                      <div />
                      <div />
                      <div />
                      <div />
                    </div>
                  </div>
                </div>
              </div>

              <div className={styles.lockCard}>🔒</div>
              <div className={styles.ticketCard}><svg width="80" height="80" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" transform="rotate(0 0 0)">
<path d="M7.65625 6.34459C7.65625 3.94514 9.60139 2 12.0008 2C14.4003 2 16.3454 3.94514 16.3454 6.34459C16.3454 8.74404 14.4003 10.6892 12.0008 10.6892C9.60139 10.6892 7.65625 8.74404 7.65625 6.34459Z" fill="#fff"></path>
<path d="M3.68359 17.439C3.68359 14.5395 6.0341 12.189 8.93359 12.189H15.0686C17.9681 12.189 20.3186 14.5395 20.3186 17.439V18.75C20.3186 19.9926 19.3113 21 18.0686 21H5.93359C4.69095 21 3.68359 19.9926 3.68359 18.75V17.439Z" fill="#fff"></path>
</svg></div>
            </div>
          </div>

          <div className={styles.leftText}>
            <h2>
              {branding?.company_name ||
                t("employee_register_left_title", {
                  defaultValue: "Create employee accounts with confidence",
                })}
            </h2>
            <p>
              {t("employee_register_left_subtitle", {
                defaultValue:
                  "Fast onboarding for your internal team with tenant branding and secure access.",
              })}
            </p>
          </div>
        </div>

        <div className={styles.rightSide}>
          <div className={styles.topBar}>
            <div className={styles.topBarRight}>
              <div className={styles.signInHint}>
                <span className={styles.signInText}>
                  {t("already_have_account", {
                    defaultValue: "Already have an account?",
                  })}
                </span>

                <Link to="/" className={styles.signInBtn}>
                  {t("sign_in", { defaultValue: "SIGN IN" })}
                </Link>
              </div>

              <LanguageSwitcher />
            </div>
          </div>

          <div className={styles.formCard}>
            <div className={styles.cardHeader}>
              <h1>
                {step === "email"
                  ? t("employee_register_email_title", {
                      defaultValue: "Enter your email",
                    })
                  : step === "code"
                  ? t("employee_register_code_title", {
                      defaultValue: "Verify your email",
                    })
                  : t("employee_register_profile_title", {
                      defaultValue: "Complete your profile",
                    })}
              </h1>

              <p>
                {step === "email"
                  ? t("employee_register_email_subtitle", {
                      defaultValue:
                        "We will send a verification code to your email address",
                    })
                  : step === "code"
                  ? t("employee_register_code_subtitle", {
                      defaultValue:
                        "Enter the code we sent to your email address",
                    })
                  : t("employee_register_profile_subtitle", {
                      defaultValue: "Finish creating your employee account",
                    })}
              </p>
            </div>

            {step === "email" && (
              <form onSubmit={handleRequestCode} className={styles.form}>
                <div className={styles.inputGroup}>
                  <label>{t("email", { defaultValue: "Email" })}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="employee@company.com"
                    required
                    disabled={formDisabled}
                  />
                </div>

                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={formDisabled}
                  style={{
                    backgroundColor: "var(--tenant-login-btn)",
                    borderColor: "var(--tenant-login-btn)",
                  }}
                >
                  {loading
                    ? t("sending", { defaultValue: "Sending..." })
                    : googleLoading
                    ? t("google_signing_in", {
                        defaultValue: "Signing in with Google...",
                      })
                    : t("send_verification_code", {
                        defaultValue: "Send code",
                      })}
                </button>

                {showSocialSection && (
                  <div className={styles.socialSection}>
                    <span className={styles.socialText}>
                      {t("create_account_with", {
                        defaultValue: "Create account with",
                      })}
                    </span>

                    <div className={styles.socialButtons}>
                      {/* <button
                        type="button"
                        className={styles.socialBtn}
                        onClick={() => handleSocialStub("Facebook")}
                        aria-label="Register with Facebook"
                        title="Facebook"
                        disabled={formDisabled}
                      >
                        f
                      </button>

                      <button
                        type="button"
                        className={styles.socialBtn}
                        onClick={() => handleSocialStub("LinkedIn")}
                        aria-label="Register with LinkedIn"
                        title="LinkedIn"
                        disabled={formDisabled}
                      >
                        in
                      </button> */}

                      <button
                        type="button"
                        className={styles.googleBtn}
                        onClick={handleGoogleRegister}
                        disabled={formDisabled}
                      >
                        <span className={styles.googleIcon}>
                          <svg viewBox="0 0 48 48">
                            <path
                              fill="#EA4335"
                              d="M24 9.5c3.54 0 6.74 1.22 9.25 3.6l6.9-6.9C35.9 2.1 30.3 0 24 0 14.6 0 6.4 5.4 2.6 13.3l8 6.2C12.5 13.2 17.8 9.5 24 9.5z"
                            />
                            <path
                              fill="#4285F4"
                              d="M46.5 24.5c0-1.6-.1-2.7-.4-3.9H24v7.4h12.7c-.3 1.8-1.9 4.6-5.4 6.5l8.3 6.5c4.8-4.4 7.6-10.8 7.6-18.5z"
                            />
                            <path
                              fill="#FBBC05"
                              d="M10.6 28.5c-.6-1.8-1-3.7-1-5.7s.4-3.9 1-5.7l-8-6.2C1 15.2 0 19.5 0 24s1 8.8 2.6 12.5l8-6z"
                            />
                            <path
                              fill="#34A853"
                              d="M24 48c6.3 0 11.6-2.1 15.4-5.7l-8.3-6.5c-2.3 1.6-5.4 2.7-7.1 2.7-6.2 0-11.5-3.7-13.4-9l-8 6C6.4 42.6 14.6 48 24 48z"
                            />
                          </svg>
                        </span>

                        <span>
                          {googleLoading
                            ? t("google_loading", { defaultValue: "Google..." })
                            : "Google"}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {success && <p className={styles.successMessage}>{success}</p>}
                {error && <p className={styles.errorMessage}>{error}</p>}
              </form>
            )}

            {step === "code" && (
              <form onSubmit={handleVerifyCode} className={styles.form}>
                <div className={styles.inputGroup}>
                  <label>{t("email", { defaultValue: "Email" })}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="employee@company.com"
                    required
                    disabled
                  />
                </div>

                <div className={styles.inputGroup}>
                  <label>
                    {t("verification_code", {
                      defaultValue: "Verification code",
                    })}
                  </label>
                  <input
                    type="text"
                    name="code"
                    value={formData.code}
                    onChange={handleChange}
                    placeholder={t("verification_code", {
                      defaultValue: "Verification code",
                    })}
                    required
                    disabled={formDisabled || isVerificationExpired}
                  />
                </div>

                <div className={styles.timerText}>
                  {isVerificationExpired
                    ? t("verification_code_expired_short", {
                        defaultValue: "Code expired",
                      })
                    : t("verification_code_expires_in", {
                        defaultValue: "Code expires in {{time}}",
                        time: formatRemainingTime(secondsLeft),
                      })}
                </div>

                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={formDisabled || isVerificationExpired}
                  style={{
                    backgroundColor: "var(--tenant-login-btn)",
                    borderColor: "var(--tenant-login-btn)",
                  }}
                >
                  {loading
                    ? t("verifying", { defaultValue: "Verifying..." })
                    : t("verify_code", {
                        defaultValue: "Verify code",
                      })}
                </button>

                <div className={styles.socialSection}>
                  <button
                    type="button"
                    className={styles.signInBtn}
                    onClick={handleResendCode}
                    disabled={formDisabled}
                  >
                    {t("resend_code", { defaultValue: "Resend code" })}
                  </button>

                  <button
                    type="button"
                    className={styles.signInBtn}
                    onClick={handleChangeEmail}
                    disabled={formDisabled}
                  >
                    {t("change_email", { defaultValue: "Change email" })}
                  </button>
                </div>

                {success && <p className={styles.successMessage}>{success}</p>}
                {error && <p className={styles.errorMessage}>{error}</p>}
              </form>
            )}

            {step === "profile" && (
              <form onSubmit={handleCompleteRegister} className={styles.form}>
                <div className={styles.inputGroup}>
                  <label>{t("email", { defaultValue: "Email" })}</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="employee@company.com"
                    required
                    disabled
                  />
                </div>

                <div className={styles.grid2}>
                  <div className={styles.inputGroup}>
                    <label>
                      {t("first_name", { defaultValue: "First Name" })}
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder={t("first_name", {
                        defaultValue: "First Name",
                      })}
                      required
                      disabled={formDisabled}
                    />
                  </div>

                  <div className={styles.inputGroup}>
                    <label>{t("last_name", { defaultValue: "Last Name" })}</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder={t("last_name", {
                        defaultValue: "Last Name",
                      })}
                      required
                      disabled={formDisabled}
                    />
                  </div>
                </div>

                <div className={styles.inputGroup}>
                  <label>{t("password", { defaultValue: "Password" })}</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder={t("password", { defaultValue: "Password" })}
                    required
                    disabled={formDisabled}
                  />
                </div>

                <button
                  type="submit"
                  className={styles.submitBtn}
                  disabled={formDisabled}
                  style={{
                    backgroundColor: "var(--tenant-login-btn)",
                    borderColor: "var(--tenant-login-btn)",
                  }}
                >
                  {loading
                    ? t("saving", { defaultValue: "Saving..." })
                    : t("employee_register_title", {
                        defaultValue: "Create Employee",
                      })}
                </button>

                <div className={styles.socialSection}>
                  <button
                    type="button"
                    className={styles.signInBtn}
                    onClick={() => setStep("code")}
                    disabled={formDisabled}
                  >
                    {t("back", { defaultValue: "Back" })}
                  </button>
                </div>

                {success && <p className={styles.successMessage}>{success}</p>}
                {error && <p className={styles.errorMessage}>{error}</p>}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
